import React, { useEffect, useMemo, useState } from 'react';
import { View, Pressable, Linking, StyleSheet, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { API_HOST, BORDER_RADIUS } from '@/constants/config';
import { isDarkTheme, BANNER_CONTENT_COLORS, WARNING_COLORS } from '@/constants/Colors';
import { bannerSurface, webInteractive, withAlpha } from '@/components/Banner/bannerChrome';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useAuthStore } from '@/store/auth';
import { subscribeAuthHydrated } from '@/lib/authHydration';
import { getSnapshot, subscribe, receiveSiteAnnouncement, visibleAnnouncement } from '@/lib/siteAnnouncements';

const DISMISS_KEY = 'gab.site-announcement.dismissed';
const storedDismissal = () => { try { return window.localStorage.getItem(DISMISS_KEY); } catch { return null; } };

const SEVERITY = {
  info: { icon: 'circle-info', accent: BANNER_CONTENT_COLORS.feature.accent },
  warning: { icon: 'triangle-exclamation', accent: { light: '#b45309', dark: WARNING_COLORS.base } },
  success: { icon: 'circle-check', accent: BANNER_CONTENT_COLORS.announcement.accent },
};

const createStyles = (colors, isDark, accent, compact) => StyleSheet.create({
  outer: { width: '100%', flexShrink: 0, paddingHorizontal: compact ? 8 : 12, paddingTop: compact ? 6 : 8, paddingBottom: 0 },
  card: { ...bannerSurface(colors, isDark, accent), position: 'relative' },
  accentRail: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, backgroundColor: accent },
  inner: {
    flexDirection: 'row', alignItems: compact ? 'flex-start' : 'center', gap: compact ? 8 : 12,
    paddingVertical: 10, paddingHorizontal: compact ? 10 : 12, paddingLeft: compact ? 12 : 14, minHeight: compact ? 0 : 44,
  },
  iconWell: {
    width: compact ? 26 : 28, height: compact ? 26 : 28, borderRadius: 14, flexShrink: 0,
    backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.14), alignItems: 'center', justifyContent: 'center',
    marginTop: compact ? 1 : 0,
  },
  content: { flex: 1, minWidth: 0, flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'stretch' : 'center', gap: compact ? 8 : 12 },
  textCol: { flex: 1, minWidth: 0, gap: 2 },
  linkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5, alignSelf: 'flex-start', flexShrink: 0,
    paddingHorizontal: compact ? 10 : 12, paddingVertical: compact ? 5 : 6, borderRadius: BORDER_RADIUS.small + 2,
    backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.12), borderWidth: 1, borderColor: withAlpha(accent, isDark ? 0.28 : 0.16),
    ...webInteractive,
  },
  linkBtnHover: { backgroundColor: withAlpha(accent, isDark ? 0.32 : 0.2) },
  dismissBtn: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', flexShrink: 0, ...webInteractive },
  dismissBtnHover: { backgroundColor: colors.solid_color_tertiary },
});

export default function SiteAnnouncementBanner() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isSM: compact } = useResponsiveness();
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus === true);
  // Audience targeting needs the persisted session; otherwise a free-only
  // notice would flash for a Plus user until their user record hydrates.
  const [authHydrated, setAuthHydrated] = useState(() => useAuthStore.persist?.hasHydrated?.() ?? true);
  useEffect(() => {
    if (authHydrated) return undefined;
    return subscribeAuthHydrated(useAuthStore.persist, () => setAuthHydrated(true));
  }, [authHydrated]);
  const [state, setState] = useState(getSnapshot);
  const [dismissed, setDismissed] = useState(storedDismissal);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const unsubscribe = subscribe(() => setState(getSnapshot()));
    setState(getSnapshot());
    return unsubscribe;
  }, []);
  useEffect(() => {
    let stopped = false;
    let controller;
    const refresh = async () => {
      if (controller) return;
      controller = new AbortController();
      const current = controller;
      const timeout = setTimeout(() => current.abort(), 8000);
      try {
        const response = await fetch(`${API_HOST}/api/site-announcement`, { cache: 'no-store', signal: current.signal });
        if (response.ok) {
          const data = await response.json();
          if (!stopped) receiveSiteAnnouncement(data);
        }
      } catch { /* Keep existing notice until its own expiry during a deploy. */ }
      finally { clearTimeout(timeout); controller = null; }
    };
    const resume = () => { setNow(Date.now()); if (document.visibilityState === 'visible') refresh(); };
    const storage = event => { if (event.key === DISMISS_KEY) setDismissed(event.newValue); };
    refresh();
    const poll = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('storage', storage);
    return () => { stopped = true; controller?.abort(); clearInterval(poll);
      document.removeEventListener('visibilitychange', resume); window.removeEventListener('storage', storage); };
  }, []);
  useEffect(() => {
    if (!state.announcement) return;
    let timer;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      const start = Date.parse(state.announcement.startsAt);
      const end = Date.parse(state.announcement.expiresAt);
      const next = start > current ? start : end;
      if (next > current) timer = setTimeout(tick, Math.min(next - current, 2147483000));
    };
    tick();
    return () => clearTimeout(timer);
  }, [state]);
  const announcement = authHydrated ? visibleAnnouncement(state, dismissed, now, { isPlus }) : null;
  const severity = SEVERITY[announcement?.severity] || SEVERITY.info;
  const accent = severity.accent[isDark ? 'dark' : 'light'];
  const styles = useMemo(() => createStyles(colors, isDark, accent, compact), [colors, isDark, accent, compact]);
  if (!announcement) return null;
  const dismiss = () => {
    setDismissed(announcement.id);
    try { window.localStorage.setItem(DISMISS_KEY, announcement.id); } catch { /* In-memory dismissal still works. */ }
  };
  const validLink = /^https:\/\//i.test(announcement.linkUrl || '');
  const linkButton = validLink ? (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={announcement.linkLabel || 'More information'}
      onPress={() => Linking.openURL(announcement.linkUrl).catch(() => {})}
      style={({ hovered, pressed }) => [styles.linkBtn, Platform.OS === 'web' && hovered && styles.linkBtnHover, pressed && { opacity: 0.88 }]}
    >
      <Text size="xs" weight="bold" style={{ color: accent }}>{announcement.linkLabel || 'More information'}</Text>
      <Icon name="arrow-up-right" size={10} color={accent} />
    </Pressable>
  ) : null;
  return (
    <View accessibilityLiveRegion="polite" style={styles.outer}>
      <View style={styles.card}>
        <View style={styles.accentRail} />
        <View style={styles.inner}>
          <View style={styles.iconWell}>
            <Icon name={severity.icon} size={compact ? 12 : 13} color={accent} solid />
          </View>
          <View style={styles.content}>
            <View style={styles.textCol}>
              <Text size="s" weight="bold" color="primary">{announcement.title}</Text>
              <Text size="xs" color="secondary">{announcement.message}</Text>
            </View>
            {linkButton}
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Dismiss announcement"
            hitSlop={6}
            onPress={dismiss}
            style={({ hovered, pressed }) => [styles.dismissBtn, Platform.OS === 'web' && hovered && styles.dismissBtnHover, pressed && { opacity: 0.85 }]}
          >
            <Icon name="xmark" size={11} color={colors.text_color_tertiary} />
          </Pressable>
        </View>
      </View>
    </View>
  );
}
